import React, { useEffect, useMemo, useState } from "react";
import ProjectCard, { audioCountOf } from "../UI/ProjectCard";
import MainContainer from "../UI/MainContainer";
import { useNavigate } from "react-router-dom";
import axios from "../../store/api/axios";
import Search from "../UI/Search";
import Select from "react-select";
import { Post, Instrument } from "../../types/Types";
import Button from "../UI/Button";
import { ButtonTypes, DEFAULT_AUTH_DATA, instrumentChipSelectStyles } from "../../utils/Constants";
import SkeletonCard from "../UI/SkeletonCard";
import EmptyState from "../UI/EmptyState";
import usePageTitle from "../../utils/usePageTitle";
import { IoAdd, IoClose } from "react-icons/io5";
import { BsGrid, BsListUl } from "react-icons/bs";

type ArchiveFilter = 'all' | 'active' | 'archived';
type SortBy = "newest" | "oldest" | "most-audio" | "title";
type View = "grid" | "list";

const STATUS_OPTIONS: { value: ArchiveFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Open' },
  { value: 'archived', label: 'Archived' },
];

const SORT_OPTIONS: { value: SortBy; label: string }[] = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "most-audio", label: "Most audio collaborations" },
  { value: "title", label: "Title A–Z" },
];

// Cards with players are heavy; render this many and add more on request.
const PAGE_SIZE = 12;
const VIEW_STORAGE_KEY = "projects-view";

const readSavedView = (): View => {
  try {
    return localStorage.getItem(VIEW_STORAGE_KEY) === "list" ? "list" : "grid";
  } catch {
    return "grid";
  }
};

const createdTime = (post: Post) => (post.created_at ? new Date(post.created_at).getTime() || 0 : 0);

const Projects: React.FC = () => {
  usePageTitle("Projects");
  const navigate = useNavigate();
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(
    null
  );
  const [postsList, setPostsList] = useState<Post[]>([]);
  const [searchInput, setSearchInput] = useState<string>("");
  // Bumped by "Clear filters" to remount <Search />, which keeps the typed
  // text in its own state.
  const [searchResetKey, setSearchResetKey] = useState(0);
  const [instruments, setInstruments] = useState<{ id: string; name: string }[]>([]);
  const [selectedInstruments, setSelectedInstruments] = useState<{ id: string; name: string }[]>([]);
  const [archiveFilter, setArchiveFilter] = useState<ArchiveFilter>('all');
  // Sorting and "Show more" work on the list that's already loaded, so they
  // don't change what's asked of the backend.
  const [sortBy, setSortBy] = useState<SortBy>("newest");
  const [view, setView] = useState<View>(readSavedView);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const authData = localStorage.getItem("auth_data") ? JSON.parse(localStorage.getItem("auth_data")!) : DEFAULT_AUTH_DATA;
  const [isLoading, setIsLoading] = useState(true);
  // Placeholder cards only on the first load; later searches keep the
  // current results on screen (dimmed) so the grid doesn't flicker while
  // typing.
  const [hasLoaded, setHasLoaded] = useState(false);

  const filtersActive = searchInput !== "" || selectedInstruments.length > 0 || archiveFilter !== 'all';

  useEffect(() => {
    // Searching while typing can have several requests in flight; a slower,
    // older one must not overwrite the newer results.
    let ignore = false;
    setIsLoading(true);
    const fetchList = async () => {
      try {
        const response = await axios.get(`/posts/?${searchInput ? "&search=" + encodeURIComponent(searchInput) : ""}${selectedInstruments.length > 0 ? "&instruments_all=" + selectedInstruments.map((item) => item.id).join(",") : ""}${archiveFilter !== 'all' ? "&is_archived=" + (archiveFilter === "archived" ? "true" : "false") : ""}`);
        if (ignore) return;
        const postListData: Post[] = response.data;
        const updatedPostListData = postListData.map((post) => {
          if (post.mp3_file && post.mp3_file.startsWith(`${process.env.BASE_URL}`)) { //????
            post.mp3_file = post.mp3_file.replace(`${process.env.BASE_URL}`, "/");
          }
          return post;
        });
        setPostsList(updatedPostListData);
        setIsLoading(false);
        setHasLoaded(true);
      } catch (error) {
        if (ignore) return;
        console.error("Error fetching user data:", error);
        setIsLoading(false);
        setHasLoaded(true);
      }
    };

    fetchList();
    return () => {
      ignore = true;
    };
  }, [searchInput, selectedInstruments, archiveFilter]);

  useEffect(() => {
    const fetchInstruments = async () => {
      try {
        const response = await axios.get('/instruments/');
        setInstruments(response.data.map((inst: Instrument) => ({ id: inst.id, name: inst.name })));
      } catch (error) {
        console.error('Error fetching instruments:', error);
      }
    };

    fetchInstruments();
  }, []);

  // New filters or sort start again from the first page of results.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [searchInput, selectedInstruments, archiveFilter, sortBy]);

  const changeView = (next: View) => {
    setView(next);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // Storage can be unavailable (private mode); the choice just isn't remembered.
    }
  };

  // "Newest first" matches the order the backend already returns
  // (created_at, newest first); Array.sort is stable, so ties keep it too.
  const sortedPosts = useMemo(() => {
    const list = [...postsList];
    if (sortBy === "newest") list.sort((a, b) => createdTime(b) - createdTime(a));
    else if (sortBy === "oldest") list.sort((a, b) => createdTime(a) - createdTime(b));
    else if (sortBy === "most-audio") list.sort((a, b) => audioCountOf(b) - audioCountOf(a));
    else list.sort((a, b) => (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base" }));
    return list;
  }, [postsList, sortBy]);
  const shownPosts = sortedPosts.slice(0, visibleCount);
  const remaining = sortedPosts.length - shownPosts.length;

  const handleAudioPlay = (audio: HTMLAudioElement) => {
    if (currentAudio && currentAudio !== audio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    }
    setCurrentAudio(audio);
  };

  const startProject = () => (authData?.id ? navigate("/post") : navigate("/login"));

  const clearSearch = () => {
    setSearchInput("");
    setSearchResetKey((key) => key + 1);
  };

  const clearFilters = () => {
    clearSearch();
    setSelectedInstruments([]);
    setArchiveFilter('all');
  };

  const removableChip = (key: string, label: string, onRemove: () => void) => (
    <button
      key={key}
      type="button"
      onClick={onRemove}
      aria-label={`Remove filter: ${label}`}
      className="flex items-center gap-1 rounded-full bg-white border border-gray-e8 px-2.5 py-0.5 text-[13px] font-semibold text-primary hover:border-primary/40"
    >
      {label} <IoClose />
    </button>
  );

  return (
    <MainContainer>
      <div className="flex flex-col min-h-screen h-full bg-white my-10 mx-auto">
        <div className="w-full flex flex-wrap justify-between items-end gap-4">
          <div>
            <h1 className="text-3xl font-bold">Projects</h1>
            <p className="mt-1 text-gray-600">Find a project to join, or start your own.</p>
          </div>
          <Button type={ButtonTypes.PRIMARY} onClick={startProject} className="!px-6 !py-3 flex items-center gap-1.5 font-semibold">
            <IoAdd className="text-xl" />New project
          </Button>
        </div>

        <div className="mt-6 bg-gray-f6 rounded-[20px] p-3.5 grid gap-3 items-end grid-cols-1 768:grid-cols-2 1200:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto_auto]">
          <div className="flex flex-col gap-1.5 min-w-0">
            <label htmlFor="projects-search" className="text-xs font-bold tracking-wide text-gray-700">Search</label>
            <Search
              key={searchResetKey}
              inputId="projects-search"
              searchWhileTyping
              placeholder="Search by title or description"
              onSearch={(searchInput) => setSearchInput(searchInput)}
              className="[&_input]:bg-white"
            />
          </div>

          <div className="flex flex-col gap-1.5 min-w-0">
            <label htmlFor="projects-instruments" className="text-xs font-bold tracking-wide text-gray-700">Instruments</label>
            <Select
              isMulti
              inputId="projects-instruments"
              name="instruments"
              placeholder="Any instrument"
              options={instruments.map((option) => ({ value: option.id, label: option.name }))}
              value={selectedInstruments.map((instrument) => ({ value: instrument.id, label: instrument.name }))}
              classNamePrefix="select"
              onChange={(selectedOptions) => {
                const selectedValues = selectedOptions.map((option: any) => ({
                  id: option.value,
                  name: option.label,
                }));
                setSelectedInstruments(selectedValues);
              }}
              styles={instrumentChipSelectStyles}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <span id="projects-status-label" className="text-xs font-bold tracking-wide text-gray-700">Status</span>
            <div role="group" aria-labelledby="projects-status-label" className="flex bg-white border border-[#D9D9D9] rounded-full p-[3px] gap-0.5 w-fit">
              {STATUS_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setArchiveFilter(option.value)}
                  aria-pressed={archiveFilter === option.value}
                  className={`rounded-full px-3.5 py-1.5 text-sm font-semibold whitespace-nowrap ${
                    archiveFilter === option.value ? "bg-primary text-white" : "text-black41 hover:bg-gray-f6"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="projects-sort" className="text-xs font-bold tracking-wide text-gray-700">Sort</label>
            {/* Same dropdown component and styles as Instruments, so both lists look alike. */}
            <Select
              inputId="projects-sort"
              name="sort"
              isSearchable={false}
              options={SORT_OPTIONS}
              value={SORT_OPTIONS.find((option) => option.value === sortBy)}
              onChange={(option) => option && setSortBy(option.value)}
              className="1200:w-[230px]"
              classNamePrefix="select"
              styles={instrumentChipSelectStyles}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 my-4 min-h-[32px] text-sm text-gray-600">
          <span aria-live="polite">
            {hasLoaded && (
              <>
                <b className="text-gray-800">
                  {postsList.length} {postsList.length === 1 ? "project" : "projects"}
                </b>
                {filtersActive && " match"}
              </>
            )}
          </span>
          {searchInput && removableChip("search", `"${searchInput}"`, clearSearch)}
          {selectedInstruments.map((instrument) =>
            removableChip(`inst-${instrument.id}`, instrument.name, () =>
              setSelectedInstruments((prev) => prev.filter((i) => i.id !== instrument.id))
            )
          )}
          {archiveFilter !== 'all' &&
            removableChip("status", STATUS_OPTIONS.find((o) => o.value === archiveFilter)!.label, () => setArchiveFilter('all'))}
          {filtersActive && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-primary font-semibold underline underline-offset-[3px] hover:text-[#526554]"
            >
              Clear filters
            </button>
          )}
          <div role="group" aria-label="View" className="ml-auto flex bg-gray-f6 rounded-xl p-[3px]">
            {([["grid", "Grid view", <BsGrid key="g" />], ["list", "List view", <BsListUl key="l" />]] as const).map(([value, label, icon]) => (
              <button
                key={value}
                type="button"
                onClick={() => changeView(value)}
                aria-pressed={view === value}
                aria-label={label}
                title={label}
                className={`w-9 h-8 grid place-items-center rounded-[9px] ${view === value ? "bg-white text-primary shadow" : "text-black41"}`}
              >
                {icon}
              </button>
            ))}
          </div>
        </div>

        {!hasLoaded ? (
          <div className="grid grid-cols-1 920:grid-cols-2 1200:grid-cols-3 gap-5 bg-secondary p-5 rounded-3xl mx-auto w-full">
            <span className="sr-only" role="status">Loading projects…</span>
            {[0, 1, 2].map((i) => (
              <SkeletonCard key={i} className="h-[380px]" />
            ))}
          </div>
        ) : (
          <div
            className={`w-full transition-opacity duration-200 ${isLoading ? "opacity-60" : ""}`}
            aria-busy={isLoading}
          >
            {postsList.length > 0 ? (
              <>
                {view === "grid" ? (
                  <div className="grid grid-cols-1 920:grid-cols-2 1200:grid-cols-3 gap-5 bg-secondary p-5 rounded-3xl mx-auto w-full">
                    {shownPosts.map((post) => (
                      <ProjectCard key={post.id} post={post} onAudioPlay={handleAudioPlay} />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col gap-2.5 bg-secondary p-3.5 rounded-3xl w-full">
                    {shownPosts.map((post) => (
                      <ProjectCard key={post.id} post={post} onAudioPlay={handleAudioPlay} variant="list" />
                    ))}
                  </div>
                )}
                {remaining > 0 && (
                  <div className="flex justify-center mt-5">
                    <button
                      type="button"
                      onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                      className="rounded-full px-5 py-2.5 font-semibold text-primary ring-[1.5px] ring-inset ring-primary hover:bg-primary/5"
                    >
                      Show more projects ({remaining} more)
                    </button>
                  </div>
                )}
              </>
            ) : filtersActive ? (
              <EmptyState
                title="No projects match these filters"
                text="Try removing an instrument or changing the search."
              >
                <Button type={ButtonTypes.PRIMARY} onClick={clearFilters} className="font-bold">
                  Clear filters
                </Button>
                <Button
                  type={ButtonTypes.DEFAULT}
                  onClick={startProject}
                  className="font-bold px-8 py-4 text-primary ring-[1.5px] ring-inset ring-primary bg-white/60 hover:bg-white"
                >
                  Start a project
                </Button>
              </EmptyState>
            ) : (
              <EmptyState title="No projects yet" text="Start the first one and invite musicians to add their audio.">
                <Button type={ButtonTypes.PRIMARY} onClick={startProject} className="font-bold">
                  Start a project
                </Button>
              </EmptyState>
            )}
          </div>
        )}
      </div>
    </MainContainer>
  );
};

export default Projects;
